import React, { PropsWithChildren } from "react";

import dayjs from "dayjs";
import "dayjs/locale/ru";
import "dayjs/locale/en";
import "dayjs/locale/zh";

import type { Locale as Locale6 } from "antd/es/locale";

import { ConfigProvider, App as AntdApp } from "antd";
import { StyleProvider } from "@ant-design/cssinjs";

import antd6LocaleRu from "antd/locale/ru_RU";
import antd6LocaleEn from "antd/locale/en_GB";
import antd6LocaleCn from "antd/locale/zh_CN";

import { useTranslation } from "react-i18next";

import { useDispatch } from "react-redux";
import App from "./containers/App";
import globalSlice from "./containers/App/reducer";
import GoogleMapsProvider from "./GoogleMapsProvider";
import exhaustiveCheck from "./utils/exhaustiveCheck";
import { SupportedLanguages } from "./viewModels/Settings/TranslationModel";
import { getV6ThemeConfig } from "./theme/v6/config";
import { useThemeMode } from "./theme/ThemeModeProvider";
import NotificationsBridge from "./components/NotificationsBridge";
import { useAppToken } from "./theme/v6/useAppToken";

const normalizeLanguage = (language?: string): SupportedLanguages => {
  const baseLanguage = language?.split("-")[0] as SupportedLanguages | undefined;

  switch (baseLanguage) {
    case "ru":
    case "en":
    case "zh":
      return baseLanguage;
    default:
      return "ru";
  }
};

const ThemedApp = ({ children }: PropsWithChildren) => {
  const { token } = useAppToken();
  return <AntdApp style={{ width: "100%", minHeight: "100vh", background: token.colorBgLayout }}>{children}</AntdApp>;
};

const AntdProvider: React.FC<PropsWithChildren<{ locale: Locale6 | undefined }>> = ({ children, locale }) => {
  const { themeMode } = useThemeMode();

  const themeConfig6 = React.useMemo(() => getV6ThemeConfig(themeMode), [themeMode]);

  return (
    <StyleProvider hashPriority="high">
      <ConfigProvider locale={locale} prefixCls="ant6" theme={themeConfig6}>
        {children}
      </ConfigProvider>
    </StyleProvider>
  );
};

// Пришлось замутить кастылек, чтобы засетать в стор язык после кго детекта, но чтобы спиннер был нужного цвета
function LocaleProvider() {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();

  const [antd6Locale, setAntd6Locale] = React.useState<Locale6>();

  React.useEffect(() => {
    const lang = normalizeLanguage(i18n.resolvedLanguage || i18n.language);

    dispatch(globalSlice.actions.setLanguage(lang));

    switch (lang) {
      case "ru":
        setAntd6Locale(antd6LocaleRu);
        dayjs.locale("ru");
        break;
      case "en":
        setAntd6Locale(antd6LocaleEn);
        dayjs.locale("en");
        break;
      case "zh":
        setAntd6Locale(antd6LocaleCn);
        dayjs.locale("zh");
        break;
      default:
        exhaustiveCheck(lang);
    }
  }, [i18n.language, i18n.resolvedLanguage, dispatch]);

  return (
    <AntdProvider locale={antd6Locale}>
      <ThemedApp>
        <NotificationsBridge />
        <GoogleMapsProvider>
          <App />
        </GoogleMapsProvider>
      </ThemedApp>
    </AntdProvider>
  );
}

export default LocaleProvider;
